import { useCallback, useContext, useEffect } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import { connect } from 'react-redux'
import { useNavigate } from 'react-router-dom'
import { rem } from 'polished'
import styled from 'styled-components'
import { fetchProductSubscription } from 'actions/LuxPlusActions'
import { useAppDispatch } from 'hooks/reduxHooks'
import GeoContext from 'contexts/geoContext'
import CurrencyContext from 'contexts/currencyContext'
import { buildUrl } from 'lib/url/searchUrlUtils'
import offerPageURL from 'lib/offer/offerPageURL'
import FormatCurrency from 'components/Common/FormatCurrency'
import Group from 'components/utils/Group'
import Pane from 'components/Common/Pane'
import LoadingIndicator from 'components/Common/Loading/LoadingIndicator'
import ModalBase from 'LuxKit/Modal/ModalBase'
import ModalBody from 'LuxKit/Modal/ModalBody'
import ModalContent from 'LuxKit/Modal/ModalContent'
import ModalFooter from 'LuxKit/Modal/ModalFooter'
import useModalElementContext from 'hooks/Modal/useModalElementContext'
import IconButton from 'LuxKit/Button/IconButton'
import LineTimesIcon from 'LuxKit/Icons/line/LineTimesIcon'
import LineInfoCircleIcon from 'LuxKit/Icons/line/LineInfoCircleIcon'
import BodyText from 'LuxKit/Typography/BodyText'
import Heading from 'LuxKit/Typography/Heading'
import TextLink from 'LuxKit/TextLink'
import Table from 'LuxKit/Tables/Table'
import TableHeader from 'LuxKit/Tables/TableHeader'
import TableHeaderRow from 'LuxKit/Tables/TableHeaderRow'
import TableHeaderCell from 'LuxKit/Tables/TableHeaderCell'
import TableBody from 'LuxKit/Tables/TableBody'
import TableBodyRow from 'LuxKit/Tables/TableBodyRow'
import TableBodyCell from 'LuxKit/Tables/TableBodyCell'
import LuxPlusLogo from 'luxPlus/components/Logos/LuxPlusLogo'
import LuxPlusSubscriptionCancellationModal from 'luxPlus/components/LuxPlusSubscriptionCancellationModal'
import { LUXURY_PLUS, LUXURY_PLUS_PATHS } from 'luxPlus/constants/base'
import { getCanRedeemLuxPlusBenefits } from 'luxPlus/selectors/featureToggle'
import { getBaseTierOfferWithJoiningFee } from 'selectors/luxPlusSelectors'
import ModalContext from 'contexts/ModalContext'
import LuxPlusMonthTakeoverBenefitsList from './LuxPlusMonthTakeoverBenefitsList'
import { LUX_PLUS_MONTH_LANDING_PAGE_URL, LUX_PLUS_MONTH_TOUR_VOUCHER_TIERS_BY_CURRENCY } from '../constants/luxPlusMonthTakeoverConstants'

const Header = styled(Pane)`
  position: relative;
`

const CloseButtonArea = styled.div`
  position: absolute;
  top: ${rem(12)};
  inset-inline-end: ${rem(12)};
`

interface Props {
  offer: App.SubscriptionOffer | undefined
  canRedeemLuxPlusBenefits: boolean
  /** Link to the tours search results, used for the "View all offers" primary action when not opened from a specific offer */
  searchLink?: string
  /** The tour offer this modal was opened from (e.g. via its campaign tag pill), if any — for "Continue to offer" */
  tourOffer?: App.AnyOffer
  /** Whether the tag was rendered on that offer's own page — swaps the CTA copy to "Return to offer" */
  onOfferPage?: boolean
}

function TourVoucherTable() {
  const intl = useIntl()
  const currency = useContext(CurrencyContext)
  const tiers = LUX_PLUS_MONTH_TOUR_VOUCHER_TIERS_BY_CURRENCY[currency] ?? LUX_PLUS_MONTH_TOUR_VOUCHER_TIERS_BY_CURRENCY.AUD

  return <Table>
    <TableHeader>
      <TableHeaderRow>
        <TableHeaderCell width="50%">
          {intl.formatMessage({
            id: 'mM4SddoB27BdO1kM6HOa',
            defaultMessage: 'Tour booking',
            description: 'Table column header for tour booking spend thresholds in the LuxPlus Month tours benefit modal',
          })}
        </TableHeaderCell>
        <TableHeaderCell width="50%">
          {intl.formatMessage({
            id: 'tK4PZsgMgbERVr8C_phi',
            defaultMessage: 'Voucher towards optional extras',
            description: 'Table column header for the optional extras voucher amount in the LuxPlus Month tours benefit modal',
          })}
        </TableHeaderCell>
      </TableHeaderRow>
    </TableHeader>
    <TableBody>
      {tiers.map(tier => (
        <TableBodyRow key={tier.spend}>
          <TableBodyCell>
            <FormattedMessage
              id="0xDhRnblOjDGH5i5pGdH"
              defaultMessage="Spend {spend}+"
              description="Table cell describing the minimum tour booking spend for a voucher tier in the LuxPlus Month tours benefit modal"
              values={{ spend: <FormatCurrency value={tier.spend} currency={currency} format="roundedDollar" /> }}
            />
          </TableBodyCell>
          <TableBodyCell>
            <FormattedMessage
              id="FUOi8Sz-A68L95nJ-t96"
              defaultMessage="{voucher} off"
              description="Table cell describing the voucher amount for a spend tier in the LuxPlus Month tours benefit modal"
              values={{ voucher: <FormatCurrency value={tier.voucher} currency={currency} format="roundedDollar" /> }}
            />
          </TableBodyCell>
        </TableBodyRow>
      ))}
    </TableBody>
  </Table>
}

function LuxPlusMonthTakeoverToursBenefitModal({ offer, canRedeemLuxPlusBenefits, searchLink, tourOffer, onOfferPage = false }: Props) {
  const intl = useIntl()
  const dispatch = useAppDispatch()
  const navigate = useNavigate()
  const { currentRegionCode } = useContext(GeoContext)
  const modalElement = useModalElementContext()
  const showModal = useContext(ModalContext)

  useEffect(() => {
    dispatch(fetchProductSubscription())
  }, [dispatch])

  const handleClose = useCallback(() => {
    modalElement.resolve()
  }, [modalElement])

  const onShowCancellationPolicy = useCallback(() => {
    showModal(<LuxPlusSubscriptionCancellationModal offer={offer} />)
  }, [showModal, offer])

  const handleContinueToOffer = useCallback(() => {
    modalElement.resolve()
    if (tourOffer && !onOfferPage) {
      navigate(buildUrl(offerPageURL(tourOffer), currentRegionCode))
    }
  }, [modalElement, tourOffer, onOfferPage, navigate, currentRegionCode])

  return <ModalBase dismissible onClose={handleClose}>
    <ModalBody>
      <Header background="lux-plus" paletteType="inverse" padding={20}>
        <CloseButtonArea>
          <IconButton
            kind="tertiary"
            variant="dark"
            onClick={handleClose}
            aria-label={intl.formatMessage({
              id: 'afr2lC45iPIVeB4P-Fvz',
              defaultMessage: 'close',
              description: 'Aria label for close button in LuxPlus Month details modal',
            })}
          >
            <LineTimesIcon />
          </IconButton>
        </CloseButtonArea>
        <Group direction="vertical" gap={12}>
          <BodyText variant="medium" colour="lux-plus-contrast">
            <FormattedMessage
              id="Ji8vprfCwBFlySy9uQXt"
              defaultMessage="Exclusive {logo} Member Event"
              description="Small label at the top of the LuxPlus Month details modal header"
              values={{ logo: <LuxPlusLogo colour="inherit" height={10} /> }}
            />
          </BodyText>
          <Heading variant="heading2" colour="lux-plus-contrast">
            <FormattedMessage
              id="Oskf6ALK80hBIqfJXJRW"
              defaultMessage="<i>Bigger</i> savings.<br></br>Same unforgettable escapes."
              description="Headline in the LuxPlus Month details modal header"
              values={{
                i: (chunks) => <i>{chunks}</i>,
                br: () => <br />,
              }}
            />
          </Heading>
        </Group>
      </Header>
      <ModalContent>
        <Group direction="vertical" gap={16}>
          {!offer && <LoadingIndicator />}
          {offer && <>
            {canRedeemLuxPlusBenefits ? (
              <BodyText variant="medium">
                <FormattedMessage
                  id="d4B8XFCAWR8qvo5dRcQ7"
                  defaultMessage="For a limited time, enjoy even more value from your membership with elevated member benefits and extra savings on select offers across hotels, flights, tours and experiences."
                  description="Intro copy in the LuxPlus Month details modal for existing members"
                />
              </BodyText>
            ) : (
              <BodyText variant="medium">
                <FormattedMessage
                  id="93ocfNPD_Yzx3mZFj4-O"
                  defaultMessage="For a limited time only, {programName} members are saving more than ever. Join now to access exclusive offers, extra savings and elevated benefits."
                  description="Intro copy in the LuxPlus Month tours benefit modal explaining the value of joining LuxPlus+"
                  values={{ programName: LUXURY_PLUS.PROGRAM_NAME }}
                />
              </BodyText>
            )}
            <BodyText variant="medium">
              <FormattedMessage
                id="dfBly6mz-ppk5ocinB9K"
                defaultMessage="Unlock a voucher to save on optional extras when you book an eligible tour. Your voucher will be available at checkout. Your {price} annual membership fee will be added to your booking total."
                description="Intro copy in the LuxPlus Month tours benefit modal explaining the tour optional extras voucher"
                values={{ price: <FormatCurrency value={offer.price} format="roundedDollar" /> }}
              />
            </BodyText>
            <TourVoucherTable />
          </>}
          {!canRedeemLuxPlusBenefits && offer && <>
            <BodyText variant="medium" weight="bold">
              <FormattedMessage
                id="phXV2_ZuR70QryBO3CZz"
                defaultMessage="{programName} unlimited benefits all year round:"
                description="Heading above the benefits list in the LuxPlus Month tours benefit modal"
                values={{ programName: LUXURY_PLUS.PROGRAM_NAME }}
              />
            </BodyText>
            <Group direction="vertical" gap={8}>
              <LuxPlusMonthTakeoverBenefitsList
                showSavingsBenefit
                joinFee={offer.joinFee}
                showAdditionalBenefits
              />
            </Group>
            <BodyText variant="small" colour="normal-contrast">
              <FormattedMessage
                id="otl0W5MUSGbt9u6zFFl8"
                defaultMessage="{programName} {price} annual membership renews on sign-up anniversary date. <cancelLink>Cancel renewal anytime.</cancelLink> <termsLink>T&Cs apply.</termsLink>"
                description="Fine print in the LuxPlus Month tours benefit modal describing membership renewal terms"
                values={{
                  programName: LUXURY_PLUS.PROGRAM_NAME,
                  price: <FormatCurrency value={offer.price} format="roundedDollar" />,
                  cancelLink: (chunks) => <TextLink underline={false} weight="bold" colour="highlight-primary-dark" size="small" endIcon={<LineInfoCircleIcon colour="normal-contrast" size="XS" />} onClick={onShowCancellationPolicy}>{chunks}</TextLink>,
                  termsLink: (chunks) => <TextLink weight="regular" size="small" to={`/${LUXURY_PLUS_PATHS.TERMS_AND_CONDITIONS_PAGE}`} onClick={handleClose}>{chunks}</TextLink>,
                }}
              />
            </BodyText>
          </>}
        </Group>
      </ModalContent>
    </ModalBody>
    <ModalFooter
      actionsLayout="stacked"
      tabletActionsLayout="auto"
      primaryActionProps={tourOffer ? {
        children: onOfferPage
          ? intl.formatMessage({
              id: 'BGrCbU4O3Bcqp2HyRGhB',
              defaultMessage: 'Return to offer',
              description: 'Primary action button in the LuxPlus Month details modal when opened from the offer\'s own page',
            })
          : intl.formatMessage({
              id: 'Ua0FbQRsVUwmY1463YwW',
              defaultMessage: 'Continue to offer',
              description: 'Primary action button in the LuxPlus Month details modal',
            }),
        onClick: handleContinueToOffer,
      } : {
        children: intl.formatMessage({
          id: 'ZpLXjW9NukTRyuhsmc1I',
          defaultMessage: 'View all offers',
          description: 'Primary action button label in the LuxPlus Month tours benefit modal, linking to tour offers filtered by the campaign tag',
        }),
        to: searchLink,
      }}
      secondaryActionProps={{
        children: intl.formatMessage({
          id: 'HLAU14j68VYupPAivcF7',
          defaultMessage: 'View all {programName} offers',
          description: 'Secondary action link in the LuxPlus Month details modal',
        }, { programName: LUXURY_PLUS.PROGRAM_NAME }),
        to: LUX_PLUS_MONTH_LANDING_PAGE_URL,
      }}
    />
  </ModalBase>
}

const mapStateToProps = (state: App.State) => ({
  offer: getBaseTierOfferWithJoiningFee(state),
  canRedeemLuxPlusBenefits: getCanRedeemLuxPlusBenefits(state),
})

export default connect(mapStateToProps)(LuxPlusMonthTakeoverToursBenefitModal)
